import { FC, useMemo } from "react"
import { Stack } from "@mui/material"
import { UserbotCatalog } from "../../interfaces"
import { useClientsQuery, useRolesQuery } from "../../store"
import { useCatalog } from "../../hooks"
import { FieldSpecInput, FieldOption } from "./fieldSpecInput"

interface IProps {
  specs: UserbotCatalog.FieldSpec[]
  values: Record<string, unknown>
  onChange: (values: Record<string, unknown>) => void
  errors?: Record<string, string>
  /** Подсказки голов для templatable-полей. */
  headHints?: string[]
}

export const FieldSpecForm: FC<IProps> = ({
  specs,
  values,
  onChange,
  errors = {},
  headHints,
}) => {
  // Отступление от спеки §5.1 (документировано в плане, Task 17 п.4): fieldSpecForm
  // ограничивается первой пачкой 100 без виртуализации/серверного поиска —
  // опции полей вспомогательные, полного списка не требуется. Eager-хуки сохранены,
  // чтобы не ломать paramsForm.test.tsx (plain-factory mock без importOriginal).
  const { data: clients } = useClientsQuery({ limit: 100 })
  const { data: roles } = useRolesQuery({ limit: 100 })
  const catalog = useCatalog()

  const optionsFor = useMemo(
    () =>
      (source?: UserbotCatalog.OptionsSource): FieldOption[] => {
        switch (source) {
          case "clients":
            return (clients?.items ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }))
          case "roles":
            return (roles?.items ?? []).map((r) => ({
              value: r.name,
              label: r.name,
            }))
          case "entities":
            return catalog.entities.map((e) => ({
              value: e.name,
              label: e.name,
            }))
          case "llmTasks":
            return catalog.llmTasks.map((t) => ({
              value: t.task,
              label: `${t.task} (${t.provider}/${t.model})`,
            }))
          case "matcherTypes":
            return catalog.matchers.map((m) => ({
              value: m.type,
              label: m.type,
            }))
          default:
            return []
        }
      },
    [clients, roles, catalog.entities, catalog.llmTasks, catalog.matchers],
  )

  const set = (key: string, value: unknown) =>
    onChange({ ...values, [key]: value })

  return (
    <Stack spacing={2}>
      {specs.map((spec) => (
        <FieldSpecInput
          key={spec.key}
          spec={spec}
          value={values[spec.key]}
          options={optionsFor(spec.optionsSource)}
          cronPresets={catalog.cronPresets}
          headHints={headHints}
          error={errors[spec.key]}
          onChange={(v) => set(spec.key, v)}
        />
      ))}
    </Stack>
  )
}
